import { styled } from '@mui/material';

import { ReactNode, useState } from 'react';

import { SimpleStatus } from '@app-velocity/common';
import { I18nResources, useI18n } from '@app-velocity/i18n';

export type WidgetTabsProps = {
  tabs: {
    title: I18nResources;
    component: ReactNode;
    status: SimpleStatus | null;
    active: boolean;
  }[];
};

const getBorderColorByStatus = (status: SimpleStatus | null) => {
  switch (status) {
    case 'error':
      return 'var(--error-error-stroke-color)';
    case 'success':
      return 'var(--success-success-stroke-color)';
    case 'pending':
      return 'var(--info-info-stroke-color)';
    case 'warning':
      return 'var(--error-error-stroke-color)';
    default:
      return 'var(--button-button-secondary-stroke-color)';
  }
};

const getBackgroundColorByStatus = (status: SimpleStatus | null) => {
  switch (status) {
    case 'error':
      return 'var(--error-error-background-color)';
    case 'success':
      return 'var(--success-success-background-color)';
    case 'pending':
      return 'var(--info-info-background-color)';
    case 'warning':
      return 'var(--error-error-background-color)';
    default:
      return 'var(--input-input-background-color)';
  }
};

const getColorByStatus = (status: SimpleStatus | null) => {
  switch (status) {
    case 'error':
      return 'var(--error-error-text-primary-color)';
    case 'success':
      return 'var(--success-success-text-primary-color)';
    case 'pending':
      return 'var(--info-info-text-primary-color)';
    case 'warning':
      return 'var(--error-error-text-primary-color)';
    default:
      return 'var(--text-text-primary-color-color)';
  }
};

const TabButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const StyledTabButton = styled('button')<{
  active: boolean;
  first: boolean;
  last: boolean;
  status: SimpleStatus | null;
}>(({ theme, first, last, status, active }) => ({
  flex: 1,
  padding: `${theme.spacing(2.5)} ${theme.spacing(4)}`,
  textAlign: 'center',
  color: 'var(--text-text-primary-color)',
  border: `1px solid ${
    active
      ? getBorderColorByStatus(status)
      : 'var(--button-button-secondary-stroke-color)'
  }`,
  backgroundColor: active
    ? getBackgroundColorByStatus(status)
    : 'var(--button-button-secondary-color)',
  borderBottom: `1px solid ${
    active
      ? getBackgroundColorByStatus(status)
      : 'var(--button-button-secondary-stroke-color)'
  }`,
  borderRightWidth: active || last ? 1 : 0,
  borderLeftWidth: active || first ? 1 : 0,
  fontSize: 13,
  fontWeight: 400,
  borderTopLeftRadius: first ? theme.spacing(2) : 0,
  borderTopRightRadius: last ? theme.spacing(2) : 0,
  cursor: 'pointer',
  zIndex: active ? 2 : 0,
}));

const StyledTabsContent = styled('div')<{
  status: SimpleStatus | null;
}>(({ theme, status }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  color: getColorByStatus(status),
  borderWidth: 1,
  borderStyle: 'solid',
  backgroundColor: getBackgroundColorByStatus(status),
  borderColor: getBorderColorByStatus(status),
  position: 'relative',
  overflow: 'hidden',
  minHeight: 108,
  zIndex: 1,
  marginTop: -1,
}));

export const WidgetTabs = ({ tabs }: WidgetTabsProps) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState(0);

  const availableTabs = tabs.filter((tab) => !!tab.active);

  const tabToDisplay = availableTabs.find((_, index) => index === activeTab);

  return (
    <div>
      <TabButtons>
        {availableTabs.map((tab, index) => (
          <StyledTabButton
            active={activeTab === index}
            first={index === 0}
            last={index === availableTabs.length - 1}
            status={tab.status}
            onClick={() => setActiveTab(index)}
          >
            {t(tab.title)}
          </StyledTabButton>
        ))}
      </TabButtons>
      {tabToDisplay ? (
        <StyledTabsContent status={tabToDisplay.status}>
          {tabToDisplay?.component}
        </StyledTabsContent>
      ) : null}
    </div>
  );
};
